import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit';
import api from '../axios';

function uid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return nanoid(36);
}

function cleanAIResponse(text) {
  if (!text) return '';
  const wrapped = text.match(/^\[\w+="([\s\S]*)"\]$/);
  if (wrapped) text = wrapped[1];
  if (/\[\/INST\]/i.test(text)) text = text.split(/\[\/INST\]/i).pop();
  text = text.replace(/\[\/?(INST|inst)\]/g, '');
  text = text.replace(/<\/s>|<\|endoftext\|>|<s>/g, '');
  // Strip model markup artifacts
  text = text.replace(/\[style\]\s*\w+\s*\[\/style\]/gi, '');
  text = text.replace(/\[style\s[^\]]*\]([\s\S]*?)\[\/style\]/gi, '$1');
  text = text.replace(/\[(replacement|emotion|benign|PRG)[^\]]*\]([\s\S]*?)\[\/\1\]/gi, '$2');
  text = text.replace(/\[\/?(style|replacement|emotion|benign|PRG)[^\]]*\]/gi, '');
  text = text.replace(/\[\s*\]/g, '');
  text = text.replace(/\s*\[[\s]*$/, '');
  const fakeTurnMatch = text.search(/\n+(?:User|Human|Assistant)\s*:/);
  if (fakeTurnMatch > 0) text = text.slice(0, fakeTurnMatch);
  return text.trim();
}

function makeConv(title) {
  return {
    _id: uid(),
    title: title || 'New conversation',
    pinned: false,
    messageCount: 0,
    lastMessageAt: null,
    createdAt: new Date().toISOString(),
  };
}

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchConversations = createAsyncThunk(
  'chat/fetchConversations',
  async (_, { getState }) => {
    try {
      const res = await api.get('/conversations');
      // API returns { results: [...] } or an array
      const list = Array.isArray(res.data) ? res.data : (res.data?.results || res.data?.conversations || []);
      return { source: 'api', list };
    } catch {
      return { source: 'local', list: getState().chat.conversations };
    }
  }
);

export const createConversation = createAsyncThunk(
  'chat/createConversation',
  async (title) => {
    try {
      const res = await api.post('/conversations', { title: title || 'New conversation' });
      return res.data?.conversation || res.data;
    } catch {
      return makeConv(title);
    }
  }
);

export const fetchConversation = createAsyncThunk(
  'chat/fetchConversation',
  async (convId, { getState }) => {
    try {
      const res = await api.get(`/conversations/${convId}`);
      return {
        conversation: res.data?.conversation || res.data,
        messages: res.data?.messages || [],
      };
    } catch {
      const state = getState().chat;
      return {
        conversation: state.conversations.find(c => c._id === convId) || null,
        messages: state.messages[convId] || [],
      };
    }
  }
);

export const deleteConversation = createAsyncThunk(
  'chat/deleteConversation',
  async (convId) => {
    try { await api.delete(`/conversations/${convId}`); } catch { /* delete locally regardless */ }
    return convId;
  }
);

export const pinConversation = createAsyncThunk(
  'chat/pinConversation',
  async ({ convId, pinned }, { getState }) => {
    const conv = getState().chat.conversations.find(c => c._id === convId);
    const updated = { ...conv, pinned };
    try { await api.patch(`/conversations/${convId}`, { pinned }); } catch { /* update locally regardless */ }
    return updated;
  }
);

export const renameConversation = createAsyncThunk(
  'chat/renameConversation',
  async ({ convId, title }, { getState }) => {
    const conv = getState().chat.conversations.find(c => c._id === convId);
    const updated = { ...conv, title };
    try { await api.patch(`/conversations/${convId}`, { title }); } catch { /* update locally regardless */ }
    return updated;
  }
);

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async ({ convId, content }, { getState, rejectWithValue, signal }) => {
    try {
      // Try conversation-scoped endpoint first (persists in DB)
      try {
        const res = await api.post(`/conversations/${convId}/messages`, { content }, { timeout: 90000, signal });
        const result = cleanAIResponse(res.data?.result || res.data?.content || '');
        const conversationTitle = res.data?.conversationTitle;
        return { convId, result, conversationTitle };
      } catch (convErr) {
        if (convErr.name === 'AbortError' || convErr.name === 'CanceledError') throw convErr;
        const status = convErr.response?.status;
        if (status && status !== 404 && status !== 503) throw convErr;
      }

      // Fallback: send full history to /ai/chat
      const history = getState().chat.messages[convId] || [];
      const messages = [...history.map(m => ({ role: m.role, content: m.content })), { role: 'user', content }]
        .filter(m => m.content?.trim());

      const res = await api.post('/ai/chat', { messages, model: 'default' }, { timeout: 90000, signal });
      return { convId, result: cleanAIResponse(res.data.result) };
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError' || err.message === 'canceled')
        return rejectWithValue('__stopped__');
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout'))
        return rejectWithValue('The model is taking longer than usual — please try again.');
      const msg = err.response?.data?.message || err.message || '';
      if (!err.response || msg === 'Network Error')
        return rejectWithValue('Could not reach the server. Check your connection and try again.');
      if (err.response?.status === 402)
        return rejectWithValue('Token budget exhausted — upgrade your plan to continue.');
      if (err.response?.status === 503)
        return rejectWithValue('AI service temporarily unavailable. Please try again in a moment.');
      return rejectWithValue(msg || 'Failed to get response');
    }
  }
);

export const rateMessageApi = createAsyncThunk(
  'chat/rateMessage',
  async ({ convId, msgId, rating }) => {
    try { await api.patch(`/conversations/${convId}/messages/${msgId}/rate`, { rating }); } catch { /* best-effort */ }
    return { convId, msgId, rating };
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    conversations: [],
    messages: {},
    error: null,
    listFetched: false,
  },
  reducers: {
    addOptimisticUserMsg: (state, action) => {
      const { convId, text, tempId } = action.payload;
      if (!state.messages[convId]) state.messages[convId] = [];
      state.messages[convId].push({
        _id: tempId, role: 'user', content: text, createdAt: new Date().toISOString(),
      });
      state.error = null;
    },
    removeOptimisticMsg: (state, action) => {
      const { convId, tempId } = action.payload;
      if (state.messages[convId]) {
        state.messages[convId] = state.messages[convId].filter(m => m._id !== tempId);
      }
    },
    addAssistantMessage: (state, action) => {
      const { convId, content } = action.payload;
      if (!content?.trim()) return;
      if (!state.messages[convId]) state.messages[convId] = [];
      state.messages[convId].push({
        _id: uid(), role: 'assistant', content, createdAt: new Date().toISOString(),
      });
      const conv = state.conversations.find(c => c._id === convId);
      if (conv) {
        conv.lastMessageAt = new Date().toISOString();
        conv.messageCount = state.messages[convId].length;
      }
    },
    clearChatError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.listFetched = true;
        const { source, list } = action.payload;
        if (source === 'api') {
          // Merge: API is authoritative for titles/pinned/counts, but keep local messages
          state.conversations = list;
        } else if (list?.length) {
          state.conversations = list;
        }
      })
      .addCase(createConversation.fulfilled, (state, action) => {
        state.conversations.unshift(action.payload);
        if (!state.messages[action.payload._id]) state.messages[action.payload._id] = [];
      })
      .addCase(fetchConversation.fulfilled, (state, action) => {
        const { conversation, messages } = action.payload;
        if (conversation) {
          const idx = state.conversations.findIndex(c => c._id === conversation._id);
          if (idx > -1) state.conversations[idx] = conversation;
          else state.conversations.unshift(conversation);
        }
        if (messages?.length) state.messages[action.payload.conversation?._id] = messages;
      })
      .addCase(deleteConversation.fulfilled, (state, action) => {
        state.conversations = state.conversations.filter(c => c._id !== action.payload);
        delete state.messages[action.payload];
      })
      .addCase(pinConversation.fulfilled, (state, action) => {
        const updated = action.payload;
        const idx = state.conversations.findIndex(c => c._id === updated._id);
        if (idx > -1) state.conversations[idx] = updated;
        state.conversations.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
      })
      .addCase(renameConversation.fulfilled, (state, action) => {
        const idx = state.conversations.findIndex(c => c._id === action.payload._id);
        if (idx > -1) state.conversations[idx].title = action.payload.title;
      })
      .addCase(sendMessage.pending, (state) => { state.error = null; })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const { convId, conversationTitle } = action.payload;
        const conv = state.conversations.find(c => c._id === convId);
        if (conv && conversationTitle && conv.title === 'New conversation') {
          conv.title = conversationTitle;
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        if (action.payload !== '__stopped__') state.error = action.payload;
      })
      .addCase(rateMessageApi.fulfilled, (state, action) => {
        const { convId, msgId, rating } = action.payload;
        const msg = (state.messages[convId] || []).find(m => m._id === msgId);
        if (msg) msg.rating = rating;
      });
  },
});

export const { addOptimisticUserMsg, removeOptimisticMsg, addAssistantMessage, clearChatError } = chatSlice.actions;
export default chatSlice.reducer;
